import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "./ProjectPage.css";

/* ==========================================================================
   MÉDIAS DU PROJET — chemins vers les images et vidéos. Les textes (titres,
   légendes, libellés) sont désormais dans fr.json / en.json sous la clé
   "menu.projectPage" et récupérés via react-i18next.
   ========================================================================== */

// Image principale (héros) en haut de page
import heroImage from "../images/ChefferieBakong.jpg";

// Galerie "Avant le projet" (carrousel automatique, 4 photos)
import avant1 from "../images/ForageBakong1.jpg";
import avant2 from "../images/ForageBakong2.jpg";
import avant3 from "../images/ForageEcolePublique.jpg";
import avant4 from "../images/ForageFoyerBakong.jpg";

// Vidéos des travaux (grille 2 x 2)
import video1 from "../videos/videoDebutTravauxPointCaptage.mp4";
import video2 from "../videos/videoNettoyagePointCaptageEauBakong.mp4";
import video3 from "../videos/videoPassageTuyauterieAlimentationChefferie.mp4";
import video4 from "../videos/placementBorneFontaineMontee.mp4";

// Galerie "Résultats des travaux" (4 photos)
import resultat1 from "../images/pointEauCarrele.jpeg";
import resultat2 from "../images/photoArriereBorneFontaine.jpeg";
import resultat3 from "../images/pointEauCarreleCarrefour.jpeg";
import resultat4 from "../images/pointEauCarreleChefferie.jpeg";

/* ==========================================================================
   UTILITAIRES
   ========================================================================== */

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (e) => setReduced(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduced;
}

/* ==========================================================================
   1) CARROUSEL AUTOMATIQUE — "Avant le projet"
   ========================================================================== */

function CarrouselAvant({ photos, onOpenLightbox }) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (paused || reducedMotion) return undefined;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % photos.length);
    }, 4000);
    return () => clearInterval(id);
  }, [paused, reducedMotion, photos.length]);

  const goTo = (i) => setIndex((i + photos.length) % photos.length);

  return (
    <div
      className="carrousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="carrousel-viewport">
        <div
          className="carrousel-track"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {photos.map((photo, i) => (
            <button
              key={photo.src}
              type="button"
              className="carrousel-slide"
              onClick={() => onOpenLightbox({ type: "image", src: photo.src, titre: photo.alt })}
              aria-label={t("menu.enlargeImage", { alt: photo.alt })}
            >
              <img src={photo.src} alt={photo.alt} loading="lazy" />
            </button>
          ))}
        </div>
      </div>

      <button
        type="button"
        className="carrousel-arrow carrousel-arrow-prev"
        onClick={() => goTo(index - 1)}
        aria-label={t("menu.prevPhoto")}
      >
        ‹
      </button>
      <button
        type="button"
        className="carrousel-arrow carrousel-arrow-next"
        onClick={() => goTo(index + 1)}
        aria-label={t("menu.nextPhoto")}
      >
        ›
      </button>

      <div className="carrousel-dots" role="tablist" aria-label={t("menu.selectPhoto")}>
        {photos.map((photo, i) => (
          <button
            key={photo.src}
            type="button"
            role="tab"
            aria-selected={i === index}
            aria-label={t("menu.goToPhoto", { index: i + 1 })}
            className={`carrousel-dot ${i === index ? "is-active" : ""}`}
            onClick={() => goTo(i)}
          />
        ))}
      </div>
    </div>
  );
}

/* ==========================================================================
   2) LIGHTBOX — agrandissement photos / vidéos
   ========================================================================== */

function Lightbox({ item, onClose }) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!item) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [item, onClose]);

  if (!item) return null;

  return (
    <div className="lightbox-backdrop" onClick={onClose}>
      <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="lightbox-close"
          onClick={onClose}
          aria-label={t("menu.closePreview")}
        >
          ×
        </button>
        {item.type === "image" ? (
          <img src={item.src} alt={item.titre || ""} />
        ) : (
          <video src={item.src} controls autoPlay playsInline />
        )}
        {item.titre && <p className="lightbox-caption">{item.titre}</p>}
      </div>
    </div>
  );
}

/* ==========================================================================
   3) FRISE CHRONOLOGIQUE — Avant → Travaux → Résultats → Impact
   ========================================================================== */

function Timeline({ etapes }) {
  return (
    <ol className="timeline">
      {etapes.map((etape, i) => (
        <li className="timeline-item" key={etape.etape}>
          <div className="timeline-marker" aria-hidden="true">
            {i + 1}
          </div>
          <div className="timeline-body">
            <span className="timeline-eyebrow">{etape.etape}</span>
            <h3 className="timeline-titre">{etape.titre}</h3>
            <p className="timeline-texte">{etape.texte}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

/* ==========================================================================
   4) COMPTEUR ANIMÉ — déclenché à l'entrée dans le viewport
   ========================================================================== */

function StatItem(stat) {
  if (stat.type === "texte") {
    return (
      <div className="compteur">
        <span className="compteur-valeur compteur-valeur-texte">{stat.valeur}</span>
        <span className="compteur-label">{stat.label}</span>
      </div>
    );
  }
  return <CompteurAnime {...stat} />;
}

function CompteurAnime({ valeur, suffixe, label }) {
  const ref = useRef(null);
  const [count, setCount] = useState(0);
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    if (reducedMotion) {
      setCount(valeur);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const duration = 1500;
            const start = performance.now();
            const animate = (now) => {
              const progress = Math.min((now - start) / duration, 1);
              const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
              setCount(Math.round(eased * valeur));
              if (progress < 1) requestAnimationFrame(animate);
            };
            requestAnimationFrame(animate);
            observer.unobserve(node);
          }
        });
      },
      { threshold: 0.4 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [valeur, reducedMotion]);

  return (
    <div className="compteur" ref={ref}>
      <span className="compteur-valeur">
        {count}
        {suffixe}
      </span>
      <span className="compteur-label">{label}</span>
    </div>
  );
}

/* ==========================================================================
   COMPOSANT PRINCIPAL — Page Projet
   ========================================================================== */

export default function ProjectPage() {
  const { t } = useTranslation();
  const [lightboxItem, setLightboxItem] = useState(null);

  const openLightbox = useCallback((item) => setLightboxItem(item), []);
  const closeLightbox = useCallback(() => setLightboxItem(null), []);

  // Contenu traduit, recalculé si la langue change (t change de référence).
  const PROJET = {
    titre: t("menu.title"),
    soustitre: t("menu.subtitleWater"),
    heroImage,

    avantProjet: [
      { src: avant1, alt: t("menu.avantAlt1") },
      { src: avant2, alt: t("menu.avantAlt2") },
      { src: avant3, alt: t("menu.avantAlt3") },
      { src: avant4, alt: t("menu.avantAlt4") },
    ],

    videos: [
      { src: video1, titre: t("menu.videoTitre1") },
      { src: video2, titre: t("menu.videoTitre2") },
      { src: video3, titre: t("menu.videoTitre3") },
      { src: video4, titre: t("menu.videoTitre4") },
    ],

    resultats: [
      { src: resultat1, alt: t("menu.resultatAlt1") },
      { src: resultat2, alt: t("menu.resultatAlt2") },
      { src: resultat3, alt: t("menu.resultatAlt3") },
      { src: resultat4, alt: t("menu.resultatAlt4") },
    ],

    timeline: [
      {
        etape: t("menu.timelineEtape1"),
        titre: t("menu.timelineTitre1"),
        texte: t("menu.timelineTexte1"),
      },
      {
        etape: t("menu.timelineEtape2"),
        titre: t("menu.timelineTitre2"),
        texte: t("menu.timelineTexte2"),
      },
      {
        etape: t("menu.timelineEtape3"),
        titre: t("menu.timelineTitre3"),
        texte: t("menu.timelineTexte3"),
      },
      {
        etape: t("menu.timelineEtape4"),
        titre: t("menu.timelineTitre4"),
        texte: t("menu.timelineTexte4"),
      },
    ],

    // type "nombre" -> compteur animé qui s'incrémente jusqu'à `valeur`.
    // type "texte"  -> affiché tel quel, sans animation.
    stats: [
      { type: "nombre", valeur: 4, suffixe: "", label: t("menu.statBornes") },
      { type: "nombre", valeur: 500, suffixe: "+", label: t("menu.statFoyers") },
      { type: "nombre", valeur: 2000, suffixe: "+", label: t("menu.statBeneficiaires") },
    ],
  };

  return (
    <div className="projet-page">
      {/* Titre + image principale */}
      <header
        className="projet-hero"
        style={{ backgroundImage: `url(${PROJET.heroImage})` }}
      >
        <div className="projet-hero-overlay">
          <h1>{PROJET.titre}</h1>
          <p>{PROJET.soustitre}</p>
        </div>
      </header>

      <main className="projet-main">
        {/* Compteurs animés */}
        <section className="projet-section" aria-label={t("menu.statsAriaLabel")}>
          <div className="compteurs-grid">
            {PROJET.stats.map((stat) => (
              <React.Fragment key={stat.label}>{StatItem(stat)}</React.Fragment>
            ))}
          </div>
        </section>

        {/* Frise chronologique */}
        <section className="projet-section">
          <h2 className="projet-section-titre">{t("menu.sectionTimeline")}</h2>
          <Timeline etapes={PROJET.timeline} />
        </section>

        {/* Carrousel "Avant le projet" */}
        <section className="projet-section">
          <h2 className="projet-section-titre">{t("menu.sectionAvant")}</h2>
          <CarrouselAvant photos={PROJET.avantProjet} onOpenLightbox={openLightbox} />
        </section>

        {/* Grille de vidéos 2x2 */}
        <section className="projet-section">
          <h2 className="projet-section-titre">{t("menu.sectionVideos")}</h2>
          <div className="video-grid">
            {PROJET.videos.map((video) => (
              <button
                type="button"
                key={video.src}
                className="video-card"
                onClick={() => openLightbox({ type: "video", src: video.src, titre: video.titre })}
                aria-label={t("menu.enlargeVideo", { titre: video.titre })}
              >
                <video src={video.src} muted playsInline preload="metadata" />
                <span className="video-play-icon" aria-hidden="true">
                  ▶
                </span>
                <span className="video-card-titre">{video.titre}</span>
              </button>
            ))}
          </div>
        </section>

        {/* Galerie "Résultats des travaux" */}
        <section className="projet-section">
          <h2 className="projet-section-titre">{t("menu.sectionResultats")}</h2>
          <div className="resultats-grid">
            {PROJET.resultats.map((photo) => (
              <button
                type="button"
                key={photo.src}
                className="resultat-card"
                onClick={() => openLightbox({ type: "image", src: photo.src, titre: photo.alt })}
                aria-label={t("menu.enlargeImage", { alt: photo.alt })}
              >
                <img src={photo.src} alt={photo.alt} loading="lazy" />
              </button>
            ))}
          </div>
        </section>
      </main>

      <Lightbox item={lightboxItem} onClose={closeLightbox} />
    </div>
  );
}